import { makeStyles } from "@fluentui/react-components";
import { primer } from "./primerDarkTheme";

export const useSidebarStyles = makeStyles({
    // shared
    card: {
        backgroundColor: primer.surface,
        borderRadius: "6px",
        border: `1px solid ${primer.border}`,
        display: "flex",
        flexDirection: "column",
    },
    activityCard: {
        backgroundColor: primer.surface,
        borderRadius: "6px",
        border: `1px solid ${primer.border}`,
        display: "flex",
        flexDirection: "column",
        overflow: "clip",
    },
    heading: { fontSize: "16px", fontWeight: 600, color: primer.fg, padding: "12px 16px" },
    headingDivider: { height: "1px", width: "100%", backgroundColor: primer.border },
    empty: { fontSize: "12px", color: primer.fgSubtle, fontStyle: "italic" as const, padding: "16px" },
    // ActivityFeed
    item: {
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        padding: "10px 14px",
        borderBottom: `1px solid ${primer.borderMuted}`,
    },
    itemContent: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: "3px",
        minWidth: 0,
        overflow: "clip",
    },
    itemTop: { lineHeight: "1.35" },
    tagGenerate: {
        display: "inline-flex",
        padding: "0 6px",
        borderRadius: "4px",
        fontSize: "10px",
        fontWeight: 600,
        lineHeight: "17.5px",
        flexShrink: 0,
        backgroundColor: primer.purpleBg,
        color: primer.purple,
    },
    tagRun: {
        display: "inline-flex",
        padding: "0 6px",
        borderRadius: "4px",
        fontSize: "10px",
        fontWeight: 600,
        lineHeight: "17.5px",
        flexShrink: 0,
        backgroundColor: "rgba(56,139,253,0.15)",
        color: primer.blue,
    },
    tagPlan: {
        display: "inline-flex",
        padding: "0 6px",
        borderRadius: "4px",
        fontSize: "10px",
        fontWeight: 600,
        lineHeight: "17.5px",
        flexShrink: 0,
        backgroundColor: "rgba(210,153,34,0.15)",
        color: primer.yellow,
    },
    tagScout: {
        display: "inline-flex",
        padding: "0 6px",
        borderRadius: "4px",
        fontSize: "10px",
        fontWeight: 600,
        lineHeight: "17.5px",
        flexShrink: 0,
        backgroundColor: "#1a4d2e",
        color: "#56d364",
    },
    tagHeal: {
        display: "inline-flex",
        padding: "0 6px",
        borderRadius: "4px",
        fontSize: "10px",
        fontWeight: 600,
        lineHeight: "17.5px",
        flexShrink: 0,
        backgroundColor: "#1f3554",
        color: primer.blue,
    },
    title: { fontSize: "13px", color: primer.fg, fontWeight: 600, lineHeight: "1.35", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const },
    actionFeed: {
        display: "flex",
        flexDirection: "column" as const,
        overflowY: "hidden" as const,
        maxHeight: "none",
    },
    detail: { fontSize: "12px", color: primer.fgMuted },
    pr: { fontSize: "12px", color: primer.blue, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const, display: "block" },
    time: { fontSize: "11px", color: primer.fgMuted, whiteSpace: "nowrap", flexShrink: 0 },
    viewAll: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px 0 12px",
        fontSize: "12px",
        fontWeight: 500,
        color: primer.fgMuted,
        cursor: "pointer",
        borderTop: `1px solid ${primer.borderMuted}`,
    },
    // ActivityLog — parsed view
    logScroll: {
        display: "flex",
        flexDirection: "column" as const,
        gap: "0px",
        maxHeight: "420px",
        overflowY: "auto" as const,
        paddingRight: "8px",
    },
    logHeader: {
        display: "flex",
        flexDirection: "column" as const,
        gap: "6px",
        paddingBottom: "10px",
        borderBottom: `1px solid ${primer.border}`,
        marginBottom: "6px",
    },
    logHeaderRow: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap" as const,
    },
    logCommandBadge: {
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase" as const,
        letterSpacing: "0.04em",
    },
    logCommandRun:     { backgroundColor: "rgba(56,139,253,0.15)",  color: primer.blue },
    logCommandPlan:    { backgroundColor: "rgba(210,153,34,0.15)",  color: primer.yellow },
    logCommandScout:   { backgroundColor: "rgba(63,185,80,0.15)",   color: primer.green },
    logCommandHeal:    { backgroundColor: "rgba(248,81,73,0.15)",   color: primer.red },
    logCommandGenerate:{ backgroundColor: primer.purpleBg,           color: primer.purple },
    logMeta: { fontSize: "11px", color: primer.fgSubtle },
    logMetaValue: { color: primer.fgMuted },
    logEntry: {
        display: "flex",
        alignItems: "flex-start",
        gap: "6px",
        padding: "5px 0",
        borderBottom: `1px solid ${primer.borderMuted}`,
    },
    logEntryLeft: {
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        gap: "3px",
        flexShrink: 0,
        width: "52px",
        paddingTop: "1px",
    },
    logTime: {
        fontSize: "10px",
        color: primer.fgSubtle,
        fontFamily: "monospace",
        whiteSpace: "nowrap" as const,
    },
    logStatusDot: {
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        flexShrink: 0,
        marginTop: "2px",
    },
    logStatusSuccess: { backgroundColor: primer.green },
    logStatusFailure: { backgroundColor: primer.red },
    logStatusPending: { backgroundColor: primer.fgSubtle },
    logEntryBody: {
        display: "flex",
        flexDirection: "column" as const,
        gap: "2px",
        minWidth: 0,
        flex: 1,
    },
    logEntryTop: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        flexWrap: "wrap" as const,
    },
    logTypeBadge: {
        display: "inline-flex",
        padding: "1px 5px",
        borderRadius: "3px",
        fontSize: "10px",
        fontWeight: 600,
        flexShrink: 0,
    },
    logTypeCreate: { backgroundColor: "rgba(56,139,253,0.12)", color: primer.blue },
    logTypeShell:  { backgroundColor: "rgba(210,153,34,0.12)", color: primer.yellow },
    logTypeRead:   { backgroundColor: "rgba(63,185,80,0.12)",  color: primer.green },
    logTypeWrite:  { backgroundColor: "rgba(248,81,73,0.12)",  color: primer.red },
    logTypeMessage:{ backgroundColor: primer.overlay, color: primer.fgMuted },
    logDetail: {
        fontSize: "11px",
        color: primer.fgMuted,
        fontFamily: "monospace",
        whiteSpace: "pre-wrap" as const,
        wordBreak: "break-all" as const,
    },
    logDuration: { fontSize: "10px", color: primer.fgSubtle, flexShrink: 0 },
    logSummary: {
        padding: "8px 0 4px",
        fontSize: "12px",
        color: primer.fg,
        fontStyle: "italic" as const,
        borderTop: `1px solid ${primer.border}`,
        marginTop: "4px",
    },
    logFooter: {
        display: "flex",
        flexWrap: "wrap" as const,
        gap: "12px",
        paddingTop: "8px",
        borderTop: `1px solid ${primer.border}`,
        marginTop: "4px",
    },
    logFooterItem: { fontSize: "11px", color: primer.fgSubtle },
    logFooterVal:  { color: primer.fgMuted, fontWeight: 500 },
    // ActionLog
    actionLink: {
        fontSize: "13px",
        color: primer.blue,
        fontWeight: 500,
        textDecoration: "none" as const,
        wordBreak: "break-word" as const,
        ":hover": { textDecoration: "underline" },
    },
    scopeLabel: { fontWeight: 600, color: primer.fgMuted },
    // QuickActions
    quickActionsContent: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "12px 14px",
    },
    actionBtn: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        padding: "10px 12px",
        borderRadius: "6px",
        backgroundColor: primer.overlay,
        border: "none",
        cursor: "pointer",
        textAlign: "left" as const,
    },
    actionLabel: { fontSize: "13px", fontWeight: 600, color: primer.blue },
    actionDesc: { fontSize: "11px", color: primer.fgMuted },
});
